<template>
  <div :class="classObject">
    <modal-opener :id="modalIdExport" :title="$i18n.t('export')" size="small" export-class="is-plain" :text="isText">
      <span v-if="isText"><i class="material-icons md-18 icon-align">download</i>{{ $i18n.t('export') }}</span>
      <span v-else class="material-icons md-18">download</span>
    </modal-opener>
    <modal :id="modalIdExport" :title="$i18n.t('export')">
      <Loader v-if="loading"></Loader>
      <div v-else>
        <choice-list
          class="d-flex flex-column"
          :list="exportableColumns"
          :value="exportColumns"
          @change="onExportChange"
          type="checkboxlist"
          fieldNameProp="export_columns"
        >
        </choice-list>
        <hr />
        <base-datalist :list="exportableTypes" :value="exportTypeValue" @change="onExportTypeChange" fieldName="export_type"></base-datalist>
        <div v-show="exportLimitShow">
          <hr />
          <base-datalist :list="exportLength" :value="exportLengthValue" @change="onExportLengthChange" fieldName="export_pages"></base-datalist>
        </div>
        <el-checkbox v-show="includeFiltersAvailable" v-model="includeFilters">
          {{ $i18n.t('include_filters') }}
        </el-checkbox>
        <div class="export-limit-information">
          {{ $i18n.t(exportLimitInformation) }}
        </div>
      </div>
      <template slot="footer">
        <el-button @click="onStartExport" v-show="exportColumns.length && !loading" type="primary"
          >{{ $i18n.t('Export') }}
        </el-button>
      </template>
    </modal>
  </div>
</template>
<script>
/**
 * @param ExportModal component
 *
 * @description listák export lehetőségeit jeleníti meg egy modalban
 */
import ChoiceList from './ChoiceList'
import BaseDatalist from '../../form/controls/BaseDatalist'
import exportList from '../../../utils/mixin-list-rest'
import { cloneDeep, get, pickBy, toLower } from 'lodash'
import { mapActions } from 'vuex'
import ModalOpener from '../../modal/Opener'
import { exportLength, exportTypes } from '../../../utils'
import notify from '../../../utils/mixin-notify'
import Loader from '../../Loader'

export default {
  name: 'ExportModal',
  mixins: [exportList, notify],
  components: {
    BaseDatalist,
    ChoiceList,
    Modal: () => import('../../modal/Modal'),
    ModalOpener,
    Loader,
  },
  data() {
    return {
      exportColumns: [],
      exportTypeValue: '',
      exportLengthValue: 'ACT',
      exportLength: exportLength(this),
      includeFilters: false,
      isText: false,
      loading: false,
    }
  },
  computed: {
    activeAggregations() {
      return this.aggregations.reduce((accumulator, { column, aggr }) => {
        if (aggr !== 'Off') {
          accumulator.push({ column, aggr })
        }
        return accumulator
      }, [])
    },
    aggregations() {
      return this.$get('aggregations')
    },
    availableColumns() {
      return this.$get('availableColumns')
    },
    classObject() {
      return {
        'el-button': !this.isText,
        'el-button--default': !this.isText,
        'el-button--small': !this.isText,
        'is-plain': !this.isText,
        'modal-container': !this.isText,
      }
    },
    collectionOrId() {
      return this.$get('collectionOrId')
    },
    columns() {
      return this.$get('columns')
    },
    columnsWithPattern() {
      return this.$get('columnsWithPattern')
    },
    currentParameters() {
      return this.$get('currentParameters')
    },
    exportableColumns() {
      return this.$get('exportableColumns')(this.$i18n.t)
    },
    exportableTypes() {
      const enabledTypes = pickBy(get(this.exportSettings, 'exportTypes', {}))
      return exportTypes(this.$i18n.t, Object.keys(enabledTypes))
    },
    exportLimitInformation() {
      return this.list.exportLimitInformation
    },
    exportLimitShow() {
      return (this.list.exportPage && this.list.exportAll) ||
          (!this.list.exportPage && !this.list.exportAll)
    },
    exportOriginalType() {
      return this.$get('exportOriginalType')
    },
    exportSettings() {
      return this.$get('exportSettings')
    },
    extendedOptions() {
      return this.$get('extendedOptions')
    },
    includeFiltersAvailable() {
      return this.exportTypeValue === 'EXCEL2007'
    },
    list() {
      return this.$get('list')
    },
    modalIdExport() {
      return `export-modal-${this.collectionOrId}`
    },
    orderParams() {
      return this.$get('orderParams')
    },
    overwrittenColumnLabels() {
      return this.columns
                 .filter((col) => col.label || col.title)
                 .map((col) => ({ data: col.columnName, title: col.label }))
    },
    pager() {
      return this.$get('pager')
    },
    panelId() {
      return this.$get('panelId')
    },
    params() {
      const {
        start,
        length,
        selectedRow,
        selectedRows,
        selectedRowsFilter,
        hiddenSearchData,
        textLimit,
        formId,
        queryId,
        processId,
        search,
        options,
        mappingOptions,
        u,
        controllerName,
        showDetails,
        rawSqlId,
        bindValues,
        conditions,
      } = this.currentParameters
      return {
        name: this.$i18n.t(controllerName),
        columns: this.exportableColumns,
        order: this.orderParams,
        start,
        length,
        total: this.pager.total,
        aggr: this.activeAggregations,
        selectedRow,
        selectedRows,
        selectedRowsFilter,
        hiddenSearchData,
        textLimit,
        formId,
        queryId,
        processId,
        panelId: this.panelId,
        rawSqlId,
        bindValues,
        conditions,
        u,
        search,
        options,
        mappingOptions,
        controllerName,
        showDetails,
        exportOriginalType: this.exportOriginalType,
        exportAll: this.list.exportAll,
        exportPage: this.list.exportPage,
        exportLimit: this.list.exportLimit,
        exportLimitInformation: this.list.exportLimitInformation,
      }
    },
  },
  mounted() {
    this.exportColumns = cloneDeep(this.exportableColumns.map((column) => column[0]).slice(0, 6))
    this.exportTypeValue = get(this.exportableTypes, 'options.0.key', '')
    this.onResize({ target: window })
    window.addEventListener('resize', this.onResize)
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    ...mapActions(['hideModal']),
    getLocale() {
      return get(this.$CORE, 'language-code', 'hu-HU')
    },
    onExportChange(column) {
      if (!this.exportColumns.includes(column)) {
        if (this.exportTypeValue === 'PDF' && this.exportColumns.length > 7) {
          this.showExcessiveColumnsNoti()
          this.exportColumns.pop()
        }
        this.exportColumns.push(column)
      } else {
        this.exportColumns.splice(this.exportColumns.indexOf(column), 1)
      }
    },
    onExportTypeChange($event) {
      this.exportTypeValue = $event.value
      if ($event.value === 'PDF' && this.exportColumns.length > 8) {
        this.showExcessiveColumnsNoti()
        this.exportColumns.splice(8)
      }
    },
    onExportLengthChange($event) {
      this.exportLengthValue = $event.value
    },
    onResize(event) {
      this.isText = event.target.innerWidth < 994
    },
    onStartExport() {
      this.loading = true
      let paraBuffer = cloneDeep(this.params)
      paraBuffer.start = this.exportLengthValue === 'ACT' ? this.pager.offset : 0
      paraBuffer['length'] = this.exportLengthValue === 'ACT' ? this.pager.limit : this.pager.total
      if (paraBuffer.exportLimit > 0 && paraBuffer['length'] > paraBuffer.exportLimit) {
        paraBuffer['length'] = paraBuffer.exportLimit
      }
      paraBuffer.order = paraBuffer.order && !paraBuffer.order.includes(null) ? paraBuffer.order : []
      paraBuffer.locale = this.getLocale()
      paraBuffer.columns = this.exportColumns
      paraBuffer.withPattern = this.columnsWithPattern
      paraBuffer.overwrittenColumnLabels = this.overwrittenColumnLabels

      const parameters = {
        params: JSON.stringify(paraBuffer),
        type: this.exportTypeValue,
      }

      let encoding, extension
      extension = toLower(this.exportTypeValue)
      switch (extension) {
        case 'excel2007':
          extension = 'xlsx'
          break
        case 'text':
          extension = 'txt'
          break
      }

      if (extension === 'xlsx') {
        parameters.includeFilters = this.includeFilters
      }
      if (parameters.type === 'csv') {
        encoding = '\ufeff'
      }

      if (paraBuffer.queryId) {
        parameters.queryId = paraBuffer.queryId
      } else if (paraBuffer.processId) {
        parameters.processId = paraBuffer.processId
      } else if (paraBuffer.formId) {
        parameters.formId = paraBuffer.formId
      }
      this.exportList(parameters).then((response) => {
        this.loading = false
        this.hideModal(this.modalIdExport)

        if (get(response, 'status') === 200) {
          const body = get(response, 'body', {})
          const param = encoding ? [encoding, body] : [body]
          const url = window.URL.createObjectURL(new Blob(param))
          let a = document.createElement('a')
          a.href = url
          a.download = `${paraBuffer.name}.${extension}`
          document.body.appendChild(a)
          a.click()
          a.remove()
        } else {
          this.showNotification({
            text: 'list_export_error',
            type: 'error',
          })
        }
      })
    },
    /**
     * Notification a tul sok oszloprol
     */
    showExcessiveColumnsNoti() {
      this.showNotification({
        text: 'maximum_columns_on_pdf_export_is_8',
        type: 'error',
      })
    },
  },
}
</script>

<style scoped>
.modal-container {
  padding: 0;
  border: none;
}
.icon-align {
  vertical-align: sub;
}
.export-limit-information {
  margin-top: 8px;
}
</style>
